import { ClientMonitoringPromptRunEntity, ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { SuggestedTermIcon } from '../Onboarding/ContentInventory/Components/TaxonomyTermNameBadge';
import { BP_POPOVER_GROUP_LABEL_CLASSES } from './brandPresenceClasses';
import {
  AuthoritySourceIndex,
  CitationHostRowLike,
  CitationSourceGroup,
  groupCitationHosts,
  resolveSourceLabel,
  ResolvedSourceLabel,
  SOURCE_CHIP_LIMIT,
  SOURCE_GROUPING_TOGGLE_HINT,
  SOURCE_GROUPING_TOGGLE_LABEL,
  selectSourceChips,
  SourceGrouping,
  UNCURATED_SOURCE_HINT,
} from './citationSources.utils';
import { SourceFavicon } from './SourceFavicon';

/**
 * The per-surface switch between grouping by website and listing every hostname.
 *
 * Identical on all three surfaces on purpose: the reader sets it once and the setting travels with
 * them through the deep link, so the answers list, the answer detail and the Analytics module never
 * disagree about how many sources an answer cited.
 */
export function SourceGroupingToggle({
  grouping,
  onChange,
  className,
}: {
  grouping: SourceGrouping;
  onChange: (next: SourceGrouping) => void;
  className?: string;
}) {
  return (
    <label
      className={`flex items-center gap-1.5 text-xs text-slate-600 mb-0 cursor-pointer ${className || ''}`}
      title={SOURCE_GROUPING_TOGGLE_HINT}
    >
      <input
        type="checkbox"
        className="cursor-pointer"
        checked={grouping === 'host'}
        onChange={(event) => onChange(event.target.checked ? 'host' : 'domain')}
      />
      {SOURCE_GROUPING_TOGGLE_LABEL}
    </label>
  );
}
/** Why a source column is empty for an answer whose citation mirror never arrived. */
const SOURCES_NOT_MEASURED_HINT = 'Which websites this answer cited has not been measured yet.';

/** Why a source column is empty for an answer that genuinely leaned on nothing. */
const SOURCES_NONE_HINT = 'This answer cited no websites.';

/** What the Sources column counts, for its header tooltip. */
export const SOURCES_COLUMN_HINT =
  'The websites this answer cited. Point at the count to see the most cited, and click one to see every answer citing it.';

/** The heading of the list that opens from a row's source count. */
export const TOP_SOURCES_HEADING = 'Most cited in this answer';

/** The name of the loading state of a row's sources. */
export const SOURCES_LOADING_LABEL = 'Loading source names';

/** "3 sources", "1 source": how many websites the answer cited, in the grouping the reader chose. */
export function sourceCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'source' : 'sources'}`;
}

/** The line under the most cited sources that says how many more the answer page lists. */
export function moreSourcesLabel(remaining: number): string {
  return `+${remaining} more on the answer page`;
}

/**
 * How long the list stays open once the pointer leaves the count — long enough to cross from the
 * count to the list without it closing under the pointer.
 */
const TOP_SOURCES_RELEASE_MS = 120;

/** Width of the list, and so how far from the frame's right edge it must start. */
const TOP_SOURCES_WIDTH_PX = 272;

/** Space kept between the list and the frame's edge. */
const TOP_SOURCES_EDGE_MARGIN_PX = 8;

/** One mark of the overlapping favicon stack: a white disc with a hairline edge. */
function FaviconDisc({ api, domain, stacked }: { api?: ISyncCoreApi; domain?: string; stacked: boolean }) {
  return (
    <span
      aria-hidden
      className={[
        'inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 border-white bg-white shadow-[0_0_0_1px_var(--color-slate-200)]',
        stacked ? '-ms-1.5' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <SourceFavicon api={api} domain={domain} />
    </span>
  );
}

/**
 * One of the most cited sources, in the list that opens from the count: the site's favicon, what
 * the source reads as (a curated name or the hostname), and how often the answer leaned on it.
 * Clicking it filters the answers list to that source; clicking the active one clears the filter.
 */
function TopSourceItem({
  api,
  resolved,
  citations,
  active,
  onSelect,
}: {
  api?: ISyncCoreApi;
  resolved: ResolvedSourceLabel;
  citations: number;
  active: boolean;
  onSelect: () => void;
}) {
  const hint = resolved.presentation.title ?? (resolved.isUncurated ? UNCURATED_SOURCE_HINT : resolved.label);
  return (
    <li>
      <button
        type="button"
        aria-pressed={active}
        title={hint}
        className={[
          'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors duration-150 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
          active ? 'bg-primary/[0.06] font-semibold text-primary' : 'text-slate-700',
          resolved.presentation.className,
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={(event) => {
          // The row is itself a link into the answer, so the source claims its click or filtering by
          // it would open the answer instead.
          event.stopPropagation();
          onSelect();
        }}
      >
        <SourceFavicon api={api} domain={resolved.key} />
        <span className="min-w-0 flex-1 truncate">{resolved.label}</span>
        {resolved.presentation.showSuggestionIcon ? <SuggestedTermIcon spacing="flush" /> : null}
        {citations > 0 ? <span className="tabular-nums text-xs text-slate-500">{citations}</span> : null}
      </button>
    </li>
  );
}

/**
 * The compact "which websites did this answer lean on?" cell: a stack of the most cited sites'
 * favicons and how many sites the answer cited. Pointing at it, or focusing it, opens the most cited
 * sources; the full list is on the answer page.
 *
 * Renders from the run's own `citationHosts` mirror — no per-row request — and regroups entirely
 * client-side when the grouping switches, because both readings come from the same payload.
 *
 * Absence and zero are told apart deliberately: a mirror that never arrived is *unmeasured* and says
 * so, while an empty mirror is the real finding that the answer cited nothing. Collapsing the two
 * into one dash would claim a measurement for every answer that predates the backfill.
 */
export function SourcesCell({
  api,
  run,
  grouping,
  authoritySources,
  sourcesReady,
  isActiveSource,
  onSelectSource,
}: {
  api?: ISyncCoreApi;
  /**
   * Only the citation mirror is read, so only that is asked for.
   *
   * `Pick`ed from the client's run entity rather than restated: a real run is assignable to it, and
   * so is a test fixture carrying nothing else.
   */
  run: Pick<ClientMonitoringPromptRunEntity, 'citationHosts'>;
  grouping: SourceGrouping;
  authoritySources: AuthoritySourceIndex;
  sourcesReady: boolean;
  isActiveSource: (group: CitationSourceGroup, termKey?: string) => boolean;
  onSelectSource: (group: CitationSourceGroup, termKey: string | undefined, active: boolean) => void;
}) {
  const mirror: readonly CitationHostRowLike[] | null | undefined = run.citationHosts;
  const groups = useMemo(() => groupCitationHosts(mirror, grouping), [mirror, grouping]);
  const listId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const releaseTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  /** Whether the focus arriving opens the list; off while Escape hands the focus back to the count. */
  const focusOpens = useRef(true);
  /** Where the open list sits on the screen; undefined while it is closed. */
  const [anchor, setAnchor] = useState<{ top: number; left: number } | undefined>(undefined);

  const cancelRelease = useCallback(() => {
    if (releaseTimer.current) {
      clearTimeout(releaseTimer.current);
      releaseTimer.current = undefined;
    }
  }, []);
  const open = useCallback(() => {
    cancelRelease();
    const box = triggerRef.current?.getBoundingClientRect();
    if (!box) {
      return;
    }
    setAnchor({
      top: Math.round(box.bottom),
      left: Math.round(
        Math.max(TOP_SOURCES_EDGE_MARGIN_PX, Math.min(box.left, window.innerWidth - TOP_SOURCES_WIDTH_PX - TOP_SOURCES_EDGE_MARGIN_PX))
      ),
    });
  }, [cancelRelease]);
  const close = useCallback(() => {
    cancelRelease();
    setAnchor(undefined);
  }, [cancelRelease]);
  const closeSoon = useCallback(() => {
    cancelRelease();
    releaseTimer.current = setTimeout(() => {
      releaseTimer.current = undefined;
      setAnchor(undefined);
    }, TOP_SOURCES_RELEASE_MS);
  }, [cancelRelease]);

  // The list is placed against the screen, so it closes rather than drift when anything scrolls.
  useEffect(() => {
    if (!anchor) {
      return undefined;
    }
    window.addEventListener('scroll', close, { capture: true, passive: true });
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, { capture: true });
      window.removeEventListener('resize', close);
    };
  }, [anchor, close]);
  useEffect(() => cancelRelease, [cancelRelease]);

  if (!mirror) {
    return (
      <span className="text-slate-300" title={SOURCES_NOT_MEASURED_HINT} aria-label={SOURCES_NOT_MEASURED_HINT}>
        —
      </span>
    );
  }

  if (!groups.length) {
    return (
      <span className="text-slate-400" title={SOURCES_NONE_HINT} aria-label={SOURCES_NONE_HINT}>
        —
      </span>
    );
  }

  // Labels come from the shared resolution, so a source reads the same here as in the Analytics
  // module. Until the curated terms land the cell holds its shape rather than showing bare hostnames.
  if (!sourcesReady) {
    return (
      <span role="img" aria-label={SOURCES_LOADING_LABEL} className="inline-flex items-center gap-2">
        <span aria-hidden className="inline-flex items-center">
          {Array.from({ length: Math.min(groups.length, SOURCE_CHIP_LIMIT) }).map((_, index) => (
            <span
              key={index}
              className={`inline-block h-5 w-5 rounded-full border-2 border-white bg-slate-100 motion-safe:animate-pulse ${index ? '-ms-1.5' : ''}`}
            />
          ))}
        </span>
        <span aria-hidden className="inline-block h-3 w-16 rounded bg-slate-100 motion-safe:animate-pulse" />
      </span>
    );
  }

  const resolvedByKey = new Map(groups.map((group) => [group.key, resolveSourceLabel(group.key, authoritySources)] as const));
  const selection = selectSourceChips(groups, {
    limit: SOURCE_CHIP_LIMIT,
    isActive: (group) => isActiveSource(group, resolvedByKey.get(group.key)?.termKey),
  });
  const filtered = selection.chips.some((group) => isActiveSource(group, resolvedByKey.get(group.key)?.termKey));

  return (
    <div
      className="inline-block"
      onMouseEnter={open}
      onMouseLeave={closeSoon}
      onFocus={() => {
        if (focusOpens.current) {
          open();
        }
      }}
      onBlur={(event) => {
        // Moving the focus from the count into the list, or along it, keeps it open.
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          close();
        }
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && anchor) {
          event.stopPropagation();
          close();
          // The focus goes back to the count without opening the list it just closed.
          focusOpens.current = false;
          triggerRef.current?.focus();
          focusOpens.current = true;
        }
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={!!anchor}
        aria-controls={listId}
        className={[
          'inline-flex items-center gap-2 rounded-lg px-1 py-0.5 text-sm transition-colors duration-150 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
          filtered ? 'font-semibold text-primary' : 'text-slate-600',
        ].join(' ')}
        onClick={(event) => {
          // Pressing the count opens the list — the one way to open it on a touch screen — and must
          // not open the answer the row links to. Leaving it, Escape or a press elsewhere closes it.
          event.stopPropagation();
          open();
        }}
      >
        <span aria-hidden className="inline-flex items-center">
          {selection.chips.map((group, index) => (
            <FaviconDisc key={group.key} api={api} domain={group.key} stacked={index > 0} />
          ))}
        </span>
        <span className="tabular-nums whitespace-nowrap">{sourceCountLabel(groups.length)}</span>
      </button>
      <div
        id={listId}
        hidden={!anchor}
        // Placed against the screen at the count's measured place, so the table's own scrolling and
        // clipping cannot cut it off; the offsets are runtime values no class can carry.
        style={anchor ? { top: anchor.top, left: anchor.left, width: TOP_SOURCES_WIDTH_PX } : undefined}
        className="fixed z-50 pt-1.5"
      >
        {/* Drawn only while open, so a closed list asks for no favicons of its own. */}
        {anchor ? (
          <div className="rounded-xl bg-white p-2 shadow-card-hover ring-1 ring-slate-200/80 motion-safe:animate-bp-fade-in">
            <div className={`${BP_POPOVER_GROUP_LABEL_CLASSES} px-2 pb-1 pt-0.5`}>{TOP_SOURCES_HEADING}</div>
            <ul className="m-0 list-none space-y-0.5 p-0">
              {selection.chips.map((group) => {
                const resolved = resolvedByKey.get(group.key)!;
                const active = isActiveSource(group, resolved.termKey);
                return (
                  <TopSourceItem
                    key={group.key}
                    api={api}
                    resolved={resolved}
                    citations={group.citations}
                    active={active}
                    onSelect={() => onSelectSource(group, resolved.termKey, active)}
                  />
                );
              })}
            </ul>
            {selection.remaining > 0 ? (
              <div className="px-2 pb-0.5 pt-1.5 text-xs text-slate-500">{moreSourcesLabel(selection.remaining)}</div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
